import SeoHead from "@/Component/Seo/SeoHead";
import Blog from "@/Component/Blog";
import {
  serverFetchBlogCategories,
  serverFetchBlogsList,
  serverFetchThingsToDoByMonthList,
} from "@/services/server";
import { getCountryFromContext } from "@/utils/getCountryFromRequest";
import {
  fetchBlogListPages,
  normalizeBlogCategoriesResponse,
  normalizeThingsToDoByMonthList,
} from "@/utils/blogHelpers";
import { fetchPageSeo } from "@/utils/pageSeo";

const MIN_BLOG_SEARCH_LENGTH = 3;
const BLOG_PAGE_SIZE = 12;

export default function BlogPage({
  seo,
  seoCountry,
  initialBlogList,
  initialBlogMeta,
  initialCategories,
  initialMonths,
  country,
}) {
  return (
    <>
      <SeoHead seo={seo} country={seoCountry} fallbackTitle="Blog | Next Holidays" />
      <Blog
        initialBlogList={initialBlogList}
        initialBlogMeta={initialBlogMeta}
        initialCategories={initialCategories}
        initialMonths={initialMonths}
        country={country}
      />
    </>
  );
}

export async function getServerSideProps(context) {
  const country = getCountryFromContext(context);
  const seoResult = await fetchPageSeo(context);
  if (seoResult.redirect) return seoResult;

  const rawSearch = String(context.query.searchText || "").trim();
  const searchText =
    rawSearch.length >= MIN_BLOG_SEARCH_LENGTH ? rawSearch : "";
  const page = Math.max(1, parseInt(context.query.page || "1", 10) || 1);

  try {
    const [blogData, categoriesResponse, monthsResponse] = await Promise.all([
      fetchBlogListPages(
        { searchText, pageSize: BLOG_PAGE_SIZE, page },
        (payload) => serverFetchBlogsList(payload, country)
      ),
      serverFetchBlogCategories(country),
      serverFetchThingsToDoByMonthList(country).catch(() => null),
    ]);

    return {
      props: {
        seo: seoResult.seo,
        seoCountry: seoResult.seoCountry,
        initialBlogList: blogData.items,
        initialBlogMeta: {
          totalCount: blogData.totalCount,
          pageIndex: blogData.pageIndex,
          totalPages: blogData.totalPages,
          pageSize: blogData.pageSize,
          searchText: blogData.searchText,
        },
        initialCategories: normalizeBlogCategoriesResponse(categoriesResponse),
        initialMonths: monthsResponse
          ? normalizeThingsToDoByMonthList(monthsResponse)
          : [],
        country,
      },
    };
  } catch (error) {
    console.error("Blog page SSR error:", error);
    return {
      props: {
        seo: seoResult.seo,
        seoCountry: seoResult.seoCountry,
        initialBlogList: [],
        initialBlogMeta: {
          totalCount: 0,
          pageIndex: 1,
          totalPages: 1,
          pageSize: BLOG_PAGE_SIZE,
          searchText: "",
        },
        initialCategories: [],
        initialMonths: [],
        country,
      },
    };
  }
}
